import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import {
  Anchor,
  Button,
  Card,
  Checkbox,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { IconBrandFacebook, IconLogin } from '@tabler/icons-react';
import { authApi } from '../../lib/auth-api';
import { trpc } from '../../lib/trpc';

const Schema = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(1, 'Password is required'),
});
type FormValues = z.infer<typeof Schema>;

const REMEMBERED_EMAIL_KEY = 'leadsync.auth.rememberedEmail';

function Login() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const search = useSearch({ strict: false }) as { error?: string };
  const [submitting, setSubmitting] = useState(false);
  const rememberedEmail =
    typeof window !== 'undefined' ? (localStorage.getItem(REMEMBERED_EMAIL_KEY) ?? '') : '';
  const [remember, setRemember] = useState(Boolean(rememberedEmail));
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: { email: rememberedEmail, password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      await authApi.login(values.email, values.password);
      if (remember) {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, values.email);
      } else {
        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      }
      // Force-refetch auth.me — the cookie is now set, the result tells
      // us which console to land on.
      await utils.auth.me.invalidate();
      const me = await utils.auth.me.fetch();
      notifications.show({ message: 'Signed in', color: 'green' });
      if (me?.roles?.includes('SuperAdmin')) {
        navigate({ to: '/central' });
      } else if (me?.roles?.includes('VendorAdmin') || me?.roles?.includes('TeamMember')) {
        navigate({ to: '/vendor' });
      } else {
        navigate({ to: '/' });
      }
    } catch (err) {
      notifications.show({
        message: (err as Error).message || 'Sign-in failed',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <Card>
        <Stack gap="sm">
          <Text fw={600} size="lg">
            Sign in
          </Text>
          {search.error && (
            <Text size="sm" c="red">
              {decodeURIComponent(search.error)}
            </Text>
          )}
          <form onSubmit={onSubmit}>
            <Stack gap="sm">
              <TextInput
                label="Email"
                autoComplete="email"
                {...register('email')}
                error={errors.email?.message}
              />
              <PasswordInput
                label="Password"
                autoComplete="current-password"
                {...register('password')}
                error={errors.password?.message}
              />
              <Checkbox
                label="Remember me"
                checked={remember}
                onChange={(e) => setRemember(e.currentTarget.checked)}
              />
              <Button type="submit" loading={submitting} leftSection={<IconLogin size={16} />}>
                Sign in
              </Button>
            </Stack>
          </form>
          <Button
            component="a"
            href={authApi.facebookLoginUrl}
            variant="default"
            leftSection={<IconBrandFacebook size={16} />}
          >
            Continue with Facebook
          </Button>
          <Group justify="space-between">
            <Anchor href="/auth/forgot-password" size="sm">
              Forgot password?
            </Anchor>
            <Anchor href="/auth/register" size="sm">
              Create account
            </Anchor>
          </Group>
        </Stack>
      </Card>
    </motion.div>
  );
}

export const Route = createFileRoute('/auth/login')({ component: Login });
